import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, Page } from '@backstage/core-components';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import { spassuCodeApiRef } from '../api';
import {
  activityConnectRouteRef,
  activityDetailRouteRef,
  controlPlaneConnectRouteRef,
  rootRouteRef,
} from '../routes';
import { normalizeRouteRefPath } from '../utils/routes';
import { ActivityCreationSurface } from './ActivityCreationSurface';
import {
  ActivityCreationGateway,
  ActivityCreationJourney,
  CONTROL_PLANE_CREATION_COPY,
  FRONTIER_CREATION_COPY,
} from './contracts';

const useCreationGateway = (): ActivityCreationGateway => {
  const api = useApi(spassuCodeApiRef);
  return useMemo(
    () => ({
      create: input => api.createActivity(input),
      workerStatus: () => api.getWorkerStatus(),
    }),
    [api],
  );
};

export const FrontierActivityCreationPage = () => {
  const navigate = useNavigate();
  const rootRoute = useRouteRef(rootRouteRef);
  const activityDetailRoute = useRouteRef(activityDetailRouteRef);
  const gateway = useCreationGateway();
  const journey = useMemo<ActivityCreationJourney>(
    () => ({
      workPath: 'frontier-web',
      ...FRONTIER_CREATION_COPY,
      showAiEngine: true,
      requireTargetBranch: false,
      branchLabel: 'Branch',
      onCreated: activity =>
        navigate(
          normalizeRouteRefPath(
            activityDetailRoute({ activityId: activity.id }),
          ),
        ),
    }),
    [activityDetailRoute, navigate],
  );

  return (
    <Page themeId="tool">
      <Header title="Nova Atividade Frontier" subtitle={journey.description} />
      <ActivityCreationSurface
        journey={journey}
        gateway={gateway}
        onClose={() => navigate(normalizeRouteRefPath(rootRoute()))}
      />
    </Page>
  );
};

export const ControlPlaneActivityCreationPage = () => {
  const navigate = useNavigate();
  const rootRoute = useRouteRef(rootRouteRef);
  const legacyConnectRoute = useRouteRef(activityConnectRouteRef);
  const controlPlaneConnectRoute = useRouteRef(controlPlaneConnectRouteRef);
  const gateway = useCreationGateway();
  const journey = useMemo<ActivityCreationJourney>(
    () => ({
      workPath: 'control-plane',
      ...CONTROL_PLANE_CREATION_COPY,
      showAiEngine: false,
      requireTargetBranch: true,
      branchLabel: 'Branch base/destino do PR',
      onCreated: activity => {
        const path = controlPlaneConnectRoute?.({ activityId: activity.id });
        navigate(
          normalizeRouteRefPath(
            path ?? legacyConnectRoute({ activityId: activity.id }),
          ),
        );
      },
    }),
    [controlPlaneConnectRoute, legacyConnectRoute, navigate],
  );

  return (
    <Page themeId="tool">
      <Header
        title="Nova Atividade Control Plane"
        subtitle={journey.description}
      />
      <ActivityCreationSurface
        journey={journey}
        gateway={gateway}
        onClose={() => navigate(normalizeRouteRefPath(rootRoute()))}
      />
    </Page>
  );
};
